/**
 * `Re-run` — `mockups/studio/money.html` §the run's section-head button.
 *
 * The mockup draws one button over a table of six clients, and `billing_run$` takes
 * **one** client and one period. So it opens on the list: what each client's run did,
 * what it wrote, and a button per row. Nothing about that is a compromise — a re-run
 * is per client in the engine, and a single button that quietly looped over six would
 * be six money calls behind one confirmation.
 *
 * The confirm names the amount and the reference, which is `.docs/20` §Design
 * decisions' rule for every `$` action. Where the first run wrote nothing there is no
 * amount, and the button says so rather than saying nothing.
 */

import { useState } from 'react'

import type { StudioMoneyView } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction } from '../../../api/studio.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { RunStepper } from '../../../components/studio/RunStepper.js'
import { Button } from '../../../components/portal/Button.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Stack } from '../../../components/portal/Layout.js'
import { ConfirmDialog, Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Muted, Num, Prose, Ref } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import type { NameOf } from './names.js'

export type RerunDialogProps = {
  readonly period: string
  readonly runs: StudioMoneyView['runs']
  /** The run table's own rows, for the total each client was billed. */
  readonly rows: StudioMoneyView['run']
  /**
   * Every client in the book, for the month nothing has been run for yet.
   *
   * `.docs/20` §Edge cases wants a `Run billing` button on that month, and a button
   * whose dialog is built out of `runs` would open on "no client has been run yet"
   * with nothing to press — which is a label, not a button. The book is the list that
   * is right before the first run and the runs are the list that is right after it.
   */
  readonly clients: readonly string[]
  readonly nameOf: NameOf
}

export function RerunDialog({ period, runs, rows, clients, nameOf }: RerunDialogProps) {
  const format = useFormat()
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<string | null>(null)

  const run = useStudioAction('billing_run')
  const review = reviewOf(run.data)

  const chosenRow = rows.find((one) => one.client === chosen) ?? null
  const chosenRun = runs.find((one) => one.client === chosen) ?? null
  const first = runs.length === 0

  /** One line per client: what its run wrote, or nothing when it has not been run. */
  const lines = first
    ? clients.map((client) => ({ client, invoiceRef: null, failed: false }))
    : runs.map((one) => ({
        client: one.client,
        invoiceRef: one.invoice_ref,
        failed: one.status === 'failed',
      }))

  return (
    <>
      <Button
        size="sm"
        onClick={() => {
          setOpen(true)
        }}
      >
        {runs.length === 0 ? 'Run billing' : 'Re-run'}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setChosen(null)
        }}
        title={`The billing run for ${format.monthLong(period)}`}
      >
        <Stack>
          <Prose>
            {first
              ? `Nothing has been billed for ${format.monthLong(period)} yet. Running a client bills them for the month; a second run against the same client and period answers with the invoice the first one made rather than billing them twice.`
              : 'A re-run is answered with the invoice the first run made — the unique constraint on (client, period) is what makes that true — so this is safe to press. It is how a run that failed part-way is finished.'}
          </Prose>

          {lines.length === 0 ? (
            <Empty>There is no client to bill.</Empty>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Client</Th>
                  <Th scope="col">Wrote</Th>
                  <Th scope="col" right>
                    Total
                  </Th>
                  {/* Blank in the mockup and worded here. axe's `empty-table-header`
                      wants *text* rather than an `aria-label`, and it is right: a
                      column of buttons in a table still needs a heading, and one word
                      is cheaper than a screen reader announcing "column 4". */}
                  <Th scope="col">{first ? 'Run' : 'Again'}</Th>
                </Tr>
              </THead>
              <TBody>
                {lines.map((one) => {
                  const row = rows.find((each) => each.client === one.client) ?? null
                  return (
                    <Tr key={one.client} variant={one.failed ? 'bad' : undefined}>
                      <Td>{nameOf(one.client)}</Td>
                      <Td>
                        {one.invoiceRef === null ? (
                          <Muted>{first ? 'not run' : 'nothing'}</Muted>
                        ) : (
                          <Ref value={one.invoiceRef} nowrap />
                        )}
                      </Td>
                      <Td right num>
                        {row?.total == null ? <Muted>—</Muted> : <Num value={row.total} />}
                      </Td>
                      <Td right>
                        <Button
                          size="sm"
                          onClick={() => {
                            /* The previous client's error and its `needs_review` notice
                               are on the same mutation; without this they open over the
                               next client as if they were about them. */
                            run.reset()
                            setChosen(one.client)
                          }}
                        >
                          {first ? 'Run' : 'Re-run'}
                        </Button>
                      </Td>
                    </Tr>
                  )
                })}
              </TBody>
            </Table>
          )}

          {runs.map((one) => (
            <Fold key={one.client} summary={`What the run did for ${nameOf(one.client)}`}>
              <Stack gap="tight">
                <RunStepper run={one} />
                {one.error === null ? null : <Notice need>{one.error}</Notice>}
              </Stack>
            </Fold>
          ))}
        </Stack>
      </Dialog>

      {chosen === null ? null : (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setChosen(null)
          }}
          title="Run billing for one client and one month"
          confirmLabel={rerunLabel(
            format.monthLong(period),
            chosenRow?.total == null ? null : format.money(chosenRow.total),
            chosenRun?.invoice_ref ?? null,
            first,
          )}
          busy={run.isPending}
          onConfirm={() => {
            run.mutate(
              { client: chosen, period, kind: 'monthly', dry_run: false },
              {
                onSuccess: (result) => {
                  if (reviewOf(result) === null) setChosen(null)
                },
              },
            )
          }}
        >
          <Stack>
            <Prose>
              {`${first ? 'Running' : 'Re-running'} ${nameOf(chosen)} for ${format.monthLong(period)}. If the month is already invoiced this answers with that invoice and writes nothing; if the run stopped part-way it finishes the steps it did not reach.`}
            </Prose>
            {review === null ? null : <NeedsReviewNotice review={review} />}
            {run.isError ? (
              <Notice need role="alert">
                {messageOf(run.error)}
              </Notice>
            ) : null}
          </Stack>
        </ConfirmDialog>
      )}
    </>
  )
}

/**
 * "Re-run August 2026 — $4,291.40 against FES-2026-047".
 *
 * `ActionForm.confirmLabel` is the same idea for a form; this is the same rule for a
 * button that has no form under it. A month with nothing billed still gets a label
 * that says what pressing it does.
 */
export function rerunLabel(
  month: string,
  amount: string | null,
  ref: string | null,
  first = false,
): string {
  const verb = first ? 'Run' : 'Re-run'
  const what = [amount, ref === null ? null : `against ${ref}`]
    .filter((part) => part !== null)
    .join(' ')
  return what === '' ? `${verb} ${month}` : `${verb} ${month} — ${what}`
}
